import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { GridBaseProps } from '@mui/material/Grid';
import { useFormContext } from 'react-hook-form';
import { useState, useEffect, useRef } from 'react';
import { Input, Select, PostalCodeInput } from '@repo/ui';

import { useMainHubStoreState } from '@/store/main-hub.store';

import { COUNTRIES } from '../helpers/countries';

interface RepresentativeAddressDataFormProps {
  withSummary?: boolean;
  containerSettings?: GridBaseProps;
  fieldsName?: {
    postCode: string;
    city: string;
    street: string;
    country: string;
  };
}

export function RepresentativeAddressDataForm({
  withSummary = true,
  containerSettings,
  fieldsName,
}: RepresentativeAddressDataFormProps) {
  const { merchantResponseData } = useMainHubStoreState();
  const { t } = useTranslation();
  const { watch, trigger } = useFormContext();

  const countryFieldName = fieldsName?.country || 'country';
  const watchedCountry = watch(countryFieldName);
  const watchedCity = watch(fieldsName?.city ?? 'city');
  const watchedPostCode = watch(fieldsName?.postCode ?? 'postCode');
  const watchedStreet = watch(fieldsName?.street ?? 'street');
  const [countryCodeValue, setCountryCodeValue] = useState(watchedCountry);
  // Pomijamy walidację przy pierwszym renderze, żeby po wejściu na krok (np. przejście
  // COMPANY_DATA → BENEFICIENT_DATA) puste pola adresu nie podkreślały się od razu.
  // Walidacja odpala się dopiero przy zmianie pola lub po kliknięciu przycisku (handleNextStep).
  const isInitialMount = useRef(true);

  useEffect(() => {
    setCountryCodeValue(watchedCountry);
  }, [watchedCountry]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;

      return;
    }
    trigger('person');
  }, [watchedCountry, watchedCity, watchedPostCode, watchedStreet]);

  if (!merchantResponseData) {
    return null;
  }

  return (
    <>
      <Grid container rowSpacing={5} columnSpacing={2} {...containerSettings}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <PostalCodeInput
            label={t('welcomePage.loanApplicationPossible.liberis.addAddressData.form.postCode')}
            name={fieldsName?.postCode || 'postCode'}
            countryCode={countryCodeValue || 'PL'}
            variant="outlined"
            // autoComplete="postal-code"
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Input
            label={t('welcomePage.loanApplicationPossible.liberis.addAddressData.form.city')}
            variant="outlined"
            name={fieldsName?.city || 'city'}
            autoComplete="address-level2"
          />
        </Grid>
        <Grid size={12}>
          <Input
            label={t('welcomePage.loanApplicationPossible.liberis.addAddressData.form.street')}
            variant="outlined"
            name={fieldsName?.street || 'street'}
            autoComplete="street-address"
          />
        </Grid>
        <Grid size={12}>
          <Select
            label={t('welcomePage.loanApplicationPossible.liberis.addAddressData.form.country')}
            name={countryFieldName}
            options={[...COUNTRIES]}
            autoComplete="country"
          />
        </Grid>
      </Grid>

      {withSummary ? (
        <Typography textAlign="center" fontSize={13} marginBottom={'45px'}>
          {t('welcomePage.loanApplicationPossible.liberis.addAddressData.formSummary')}
        </Typography>
      ) : null}
    </>
  );
}
